// Utilitaires pour Hangar & Lease Analytics

/**
 * Calcule le taux d'occupation
 * @param {Array} hangars - Liste des hangars
 * @returns {number} Taux d'occupation en pourcentage
 */
export const calculateOccupancyRate = (hangars) => {
  if (!hangars.length) return 0;
  const occupied = hangars.filter(h => h.status === 'occupied').length;
  return Math.round((occupied / hangars.length) * 100);
};

/**
 * Récupère les baux expirant bientôt
 * @param {Array} leases - Liste des baux
 * @param {number} days - Nombre de jours
 * @returns {Array} Baux filtrés
 */
export const getExpiringLeases = (leases, days = 30) => {
  if (!leases.length) return [];
  const today = new Date();
  const threshold = new Date();
  threshold.setDate(today.getDate() + days);
  
  return leases.filter(lease => {
    if (!lease.end_date) return false;
    const endDate = new Date(lease.end_date);
    return endDate >= today && endDate <= threshold;
  }).sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
};

/**
 * Récupère les loyers en retard
 * @param {Array} leases - Liste des baux avec last_rent_paid_date
 * @returns {Array} Baux en retard
 */
export const getRentArrears = (leases) => {
  if (!leases.length) return [];
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  return leases.filter(lease => {
    if (lease.status !== 'active') return false;
    if (!lease.last_rent_paid_date) return true;
    
    const lastPaid = new Date(lease.last_rent_paid_date);
    const lastPaidMonth = lastPaid.getMonth();
    const lastPaidYear = lastPaid.getFullYear();
    
    // Si dernier paiement avant le mois courant
    return (lastPaidYear < currentYear) || 
           (lastPaidYear === currentYear && lastPaidMonth < currentMonth);
  });
};

/**
 * Calcule le total des dépôts de garantie
 * @param {Array} leases - Liste des baux
 * @returns {number} Total des dépôts
 */
export const getTotalDeposits = (leases) => {
  if (!leases.length) return 0;
  return leases.reduce((sum, lease) => sum + (lease.security_deposit || 0), 0);
};

/**
 * Calcule le revenu mensuel total des baux actifs
 * @param {Array} leases - Liste des baux
 * @returns {number} Revenu mensuel total
 */
export const getMonthlyRevenue = (leases) => {
  if (!leases.length) return 0;
  return leases
    .filter(l => l.status === 'active')
    .reduce((sum, lease) => sum + (lease.monthly_rent || 0), 0);
};

/**
 * Calcule la surface totale louée
 * @param {Array} hangars - Liste des hangars
 * @returns {number} Surface totale en sqft
 */
export const getTotalLeasedArea = (hangars) => {
  if (!hangars.length) return 0;
  return hangars
    .filter(h => h.status === 'occupied')
    .reduce((sum, hangar) => sum + (hangar.surface_sqft || 0), 0);
};

/**
 * Récupère les assurances expirant bientôt
 * @param {Array} leases - Liste des baux
 * @param {number} days - Nombre de jours
 * @returns {Array} Assurances expirant
 */
export const getExpiringInsurances = (leases, days = 60) => {
  if (!leases.length) return [];
  const today = new Date();
  const threshold = new Date();
  threshold.setDate(today.getDate() + days);
  
  return leases.filter(lease => {
    if (!lease.insurance_expiry_date) return false;
    const expiryDate = new Date(lease.insurance_expiry_date);
    return expiryDate >= today && expiryDate <= threshold;
  }).sort((a, b) => new Date(a.insurance_expiry_date) - new Date(b.insurance_expiry_date));
};

/**
 * Récupère les taxes locales impayées
 * @param {Array} hangars - Liste des hangars
 * @returns {Array} Taxes impayées
 */
export const getUnpaidTaxes = (hangars) => {
  if (!hangars.length) return [];
  return hangars.filter(hangar => {
    return hangar.local_tax_due_date && 
           hangar.local_tax_amount > 0 && 
           !hangar.local_tax_paid &&
           new Date(hangar.local_tax_due_date) < new Date();
  });
};

/**
 * Prépare les données pour export XLSX (hangars)
 * @param {Array} hangars - Liste des hangars
 * @returns {Array} Données formatées
 */
export const formatHangarsForExport = (hangars) => {
  return hangars.map(h => ({
    'Hangar Number': h.hangar_number,
    'Name': h.name || '',
    'Location': h.location || '',
    'Surface (sqft)': h.surface_sqft,
    'Status': h.status,
    'Monthly Rent': h.monthly_rent,
    'Current Tenant': h.current_tenant_name || '',
    'Insurance Expiry': h.insurance_expiry_date || '',
    'Tax Due Date': h.local_tax_due_date || '',
    'Tax Paid': h.local_tax_paid ? 'Yes' : 'No'
  }));
};

/**
 * Prépare les données pour export XLSX (leases)
 * @param {Array} leases - Liste des baux
 * @returns {Array} Données formatées
 */
export const formatLeasesForExport = (leases) => {
  return leases.map(l => ({
    'Lease Number': l.lease_number,
    'Tenant': l.tenant_name || '',
    'Hangar': l.hangar_number || '',
    'Start Date': l.start_date,
    'End Date': l.end_date || 'Open-ended',
    'Monthly Rent': l.monthly_rent,
    'Security Deposit': l.security_deposit,
    'Insurance Expiry': l.insurance_expiry_date || '',
    'Status': l.status,
    'Last Rent Paid': l.last_rent_paid_date || 'Never',
    'Arrears Amount': l.rent_arrears || 0
  }));
};

/**
 * Prépare les données pour export financier
 * @param {Object} financialData - Données financières
 * @returns {Array} Données formatées
 */
export const formatFinancialForExport = (financialData) => {
  return [
    { 'Metric': 'Total Hangars', 'Value': financialData.totalHangars },
    { 'Metric': 'Occupied Hangars', 'Value': financialData.occupiedHangars },
    { 'Metric': 'Occupancy Rate', 'Value': `${financialData.occupancyRate}%` },
    { 'Metric': 'Active Leases', 'Value': financialData.activeLeases },
    { 'Metric': 'Monthly Revenue', 'Value': financialData.monthlyRevenue },
    { 'Metric': 'Total Security Deposits', 'Value': financialData.totalDeposits },
    { 'Metric': 'Total Rent Arrears', 'Value': financialData.totalArrears },
    { 'Metric': 'Expiring Leases (30 days)', 'Value': financialData.expiringLeasesCount },
    { 'Metric': 'Expiring Insurances (60 days)', 'Value': financialData.expiringInsurancesCount }
  ];
};

/**
 * Filtre global (recherche "n'importe quoi")
 * @param {Array} hangars - Liste des hangars
 * @param {Array} leases - Liste des baux
 * @param {string} searchTerm - Terme de recherche
 * @returns {Object} Objet avec hangars et leases filtrés
 */
export const globalSearch = (hangars, leases, searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return { filteredHangars: hangars, filteredLeases: leases };
  }
  
  const term = searchTerm.toLowerCase().trim();
  
  const filteredHangars = hangars.filter(h => 
    (h.hangar_number && h.hangar_number.toLowerCase().includes(term)) ||
    (h.name && h.name.toLowerCase().includes(term)) ||
    (h.location && h.location.toLowerCase().includes(term)) ||
    (h.current_tenant_name && h.current_tenant_name.toLowerCase().includes(term))
  );
  
  const filteredLeases = leases.filter(l =>
    (l.lease_number && l.lease_number.toLowerCase().includes(term)) ||
    (l.tenant_name && l.tenant_name.toLowerCase().includes(term)) ||
    (l.hangar_number && l.hangar_number.toLowerCase().includes(term)) ||
    (l.aircraft_tail && l.aircraft_tail.toLowerCase().includes(term))
  );
  
  return { filteredHangars, filteredLeases };
};